import { isGeojsonPerfLogEnabled } from "@/feature-flags/non-hook/non-hook";

const PERF_BASE = "customgeojson-perf";

type Mode = "backend" | "frontend";
type PerfMeta = Record<string, unknown>;

// Keep metadata out of PerformanceEntry to avoid relying on browser-specific support for `detail`.
const metaByMeasureName = new Map<string, PerfMeta>();

// Run counter (per page load). This is enough for repeated measurements on staging.
let runCounter = 0;

function nextRunId(): number {
  runCounter += 1;
  return runCounter;
}

function makeMarkName(args: { op: string; mode: Mode; run: number; mark: string }): string {
  // Example: customgeojson-perf:load:backend:run=3:mark=tFetchDone
  return `${PERF_BASE}:${args.op}:${args.mode}:run=${args.run}:mark=${args.mark}`;
}

function makeMeasureName(args: { op: string; mode: Mode; run: number; phase: string }): string {
  // Example: customgeojson-perf:load:backend:run=3:phase=fetch
  return `${PERF_BASE}:${args.op}:${args.mode}:run=${args.run}:phase=${args.phase}`;
}

export type PerfRun = {
  enabled: boolean;
  run: number;
  op: string;
  mode: Mode;
  mark: (mark: string) => void;
  addMeta: (meta: PerfMeta) => void;
  // Measures a predefined set of phases if the corresponding marks exist.
  finish: (phases: Array<{ phase: string; startMark: string; endMark: string }>) => void;
  // Clears marks for this run.
  clear: () => void;
};

export function createPerfRun(args: { op: string; mode: Mode; meta?: PerfMeta }): PerfRun {
  const started = perfStartRun(args);
  const enabled = started.run >= 0;

  return {
    enabled,
    run: started.run,
    op: args.op,
    mode: args.mode,
    mark: (mark: string) => {
      if (!enabled) {
        return;
      }
      perfMark({ op: args.op, mode: args.mode, run: started.run, mark });
    },
    addMeta: (meta: PerfMeta) => {
      if (!enabled) {
        return;
      }
      const key = `${PERF_BASE}:${args.op}:${args.mode}:run=${started.run}:meta`;
      const prev = metaByMeasureName.get(key) ?? {};
      metaByMeasureName.set(key, { ...prev, ...meta });
    },
    finish: (phases) => {
      if (!enabled) {
        return;
      }

      // Only measure phases where both marks exist (avoid runtime errors if a path short-circuits).
      const existingMarks = new Set(
        performance
          .getEntriesByType("mark")
          .filter((m) => m.name.startsWith(`${PERF_BASE}:${args.op}:${args.mode}:run=${started.run}:mark=`))
          .map((m) => m.name),
      );

      for (const p of phases) {
        const start = makeMarkName({ op: args.op, mode: args.mode, run: started.run, mark: p.startMark });
        const end = makeMarkName({ op: args.op, mode: args.mode, run: started.run, mark: p.endMark });
        if (!existingMarks.has(start) || !existingMarks.has(end)) {
          continue;
        }
        perfMeasure({
          op: args.op,
          mode: args.mode,
          run: started.run,
          phase: p.phase,
          startMark: p.startMark,
          endMark: p.endMark,
        });
      }
    },
    clear: () => {
      if (!enabled) {
        return;
      }
      perfClearRun({ op: args.op, mode: args.mode, run: started.run });
    },
  };
}

export function perfStartRun(args: { op: string; mode: Mode; meta?: PerfMeta }): { run: number } {
  if (!isGeojsonPerfLogEnabled()) {
    return { run: -1 };
  }

  const run = nextRunId();
  performance.mark(makeMarkName({ op: args.op, mode: args.mode, run, mark: "t0" }));

  // Store run-level metadata (keyed by a synthetic name) so it can be exported alongside measures.
  metaByMeasureName.set(`${PERF_BASE}:${args.op}:${args.mode}:run=${run}:meta`, args.meta ?? {});

  return { run };
}

export function perfMark(args: { op: string; mode: Mode; run: number; mark: string }): void {
  if (args.run < 0) {
    return;
  }
  performance.mark(makeMarkName({ op: args.op, mode: args.mode, run: args.run, mark: args.mark }));
}

export function perfMeasure(args: {
  op: string;
  mode: Mode;
  run: number;
  phase: string;
  startMark: string;
  endMark: string;
  meta?: PerfMeta;
}): void {
  if (args.run < 0) {
    return;
  }

  const start = makeMarkName({ op: args.op, mode: args.mode, run: args.run, mark: args.startMark });
  const end = makeMarkName({ op: args.op, mode: args.mode, run: args.run, mark: args.endMark });
  const name = makeMeasureName({ op: args.op, mode: args.mode, run: args.run, phase: args.phase });

  // Create the PerformanceEntry (PerformanceMeasure) for later inspection.
  performance.measure(name, start, end);

  // Keep metadata (if any) for export.
  if (args.meta) {
    metaByMeasureName.set(name, args.meta);
  }

  // Clean up marks to avoid unbounded memory growth if this is used heavily.
  // Removed clearing marks here to avoid breaking chained measures.
}

export function exportPerf(prefix: string = PERF_BASE): Array<{
  name: string;
  startTime: number;
  duration: number;
  meta: PerfMeta;
}> {
  const entries = performance.getEntriesByType("measure").filter((e) => e.name.startsWith(prefix));

  return entries.map((e) => {
    // Measure name format:
    //   customgeojson-perf:<op>:<mode>:run=<n>:phase=<phase>
    // Run meta is stored as:
    //   customgeojson-perf:<op>:<mode>:run=<n>:meta
    const m = e.name.match(/^([^:]+):([^:]+):([^:]+):run=(\d+):phase=/);
    const runMetaKey = m ? `${m[1]}:${m[2]}:${m[3]}:run=${m[4]}:meta` : null;

    const runMeta = (runMetaKey && metaByMeasureName.get(runMetaKey)) ?? {};
    const measureMeta = metaByMeasureName.get(e.name) ?? {};

    return {
      name: e.name,
      startTime: e.startTime,
      duration: e.duration,
      meta: { ...runMeta, ...measureMeta },
    };
  });
}

function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadPerfJson(prefix: string = PERF_BASE): void {
  const data = exportPerf(prefix);
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  downloadJson(`${prefix}-${ts}.json`, data);
}

export function perfClearRun(args: { op: string; mode: Mode; run: number }): void {
  if (args.run < 0) {
    return;
  }

  const prefix = `${PERF_BASE}:${args.op}:${args.mode}:run=${args.run}:mark=`;
  const marks = performance.getEntriesByType("mark").filter((m) => m.name.startsWith(prefix));

  for (const m of marks) {
    performance.clearMarks(m.name);
  }
}

// Convenience global hooks for staging debugging.
// Usage in devtools:
//   window.__customGeojsonPerfExport()
//   window.__customGeojsonPerfDownload()
declare global {
  interface Window {
    __customGeojsonPerfExport?: () => ReturnType<typeof exportPerf>;
    __customGeojsonPerfDownload?: () => void;
  }
}

if (typeof window !== "undefined") {
  window.__customGeojsonPerfExport = () => exportPerf(PERF_BASE);
  window.__customGeojsonPerfDownload = () => downloadPerfJson(PERF_BASE);
}
